/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useRef } from 'react';
import styled, { css } from 'styled-components';
import { mediaQuery } from '@99designs/emu/css';
import TileContainer, { StyledContainer } from './Container';
import { StyledFooter } from './Footer';
import { COLOR } from './constants';
import { TileProps } from './interfaces';

export default function Tile(props: TileProps) {
    const ref = useRef<HTMLDivElement>(null);

    return (
        <TileWrapper
            ref={ref}
            {...props}
            style={{
                ...props.style,
                backgroundColor: `${props.color}`,
            }}
            onClick={() => {
                ref.current?.focus();
            }}
            onMouseLeave={() => {
                ref.current?.blur();
            }}
            // tabIndex="0" // use this if you want all tiles selectable
            tabIndex={props.index} // this only linked tiles selectable
        >
            <TileContainer {...props} />
        </TileWrapper>
    );
}

const breakpoints = {
    phonesPortrait: 420,
};

const TileWrapper = styled.div<TileProps>`
    width: 100%;
    position: relative;
    margin-bottom: 5px;
    overflow: hidden;
    outline: none;
    background-color: lighten(${COLOR.GREY}, 35%);

    /*
    TODO:
    In our mediaQuery lib "small" breakpoint is [0, 599].
    To make this code block work with our mediaQuery lib 
    we might need to update our lib to support something like xsmall and small.
    */
    @media only screen and (min-width: ${breakpoints.phonesPortrait}px) {
        width: 48%;
        width: calc(50% - 5px);
        margin-left: 5px;
        float: left;
    }

    ${mediaQuery.minimum('normal')} {
        width: 32%;
        width: calc(33.3% - 5px);
        margin-left: 5px;
    }

    &:nth-child(2n + 1) {
        ${mediaQuery.between('small', 'medium')} {
            margin-left: 0;
        }
    }

    &:nth-child(3n + 1) {
        ${mediaQuery.minimum('normal')} {
            margin-left: 0;
        }
    }

    ${(props) =>
        props.large &&
        css`
            ${mediaQuery.minimum('normal')} {
                width: calc(66.5% - 5px);
            }
        `}

    ${(props) =>
        props.betweenLarge &&
        css`
            ${mediaQuery.minimum('normal')} {
                margin-top: -33.3%;
                margin-left: 0;
            }
        `}

    ${(props) =>
        props.red &&
        css`
            background: ${COLOR.RED} !important;
            color: ${COLOR.WHITE};
            .tile__text {
                color: ${COLOR.WHITE};
            }
        `}

    ${(props) =>
        props.white &&
        css`
            background: ${COLOR.WHITE};
            color: ${COLOR.RED};
            .tile__title {
                color: ${COLOR.RED} !important;
            }
            .tile__link {
                background: transparent;
                color: ${COLOR.RED} !important;
            }
        `}

    &::before {
        width: 0;
        height: 0;
        float: left;
        padding-bottom: 100%;
        content: '';
        display: block;
    }

    &:hover ${StyledContainer}, &:focus ${StyledContainer} {
        &::before {
            ${(props) =>
                props.link &&
                (props.image || props.video) &&
                css`
                    opacity: 1;
                `}
        }

        .tile__video,
        .tile__title,
        .tile__text--hover {
            opacity: 1 !important;
        }

        .tile__index {
            background: ${COLOR.RED};
            transition: background 300ms cubic-bezier(0.19, 1, 0.22, 1);
        }

        ${StyledFooter} {
            opacity: 1 !important;
        }
    }
`;
